import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RiArrowLeftLine, RiCheckboxCircleLine } from 'react-icons/ri';
import { Button, Card, Skeleton } from '@/components/ui';
import { SEO } from '@/components/seo/SEO';
import { StatusBadge } from '@/components/worker/StatusBadge/StatusBadge';
import { RewardDisplay } from '@/components/job/RewardDisplay/RewardDisplay';
import { JobBadge } from '@/components/job/JobBadge/JobBadge';
import { ProofSchemaForm } from '@/components/job/ProofSchemaForm/ProofSchemaForm';
import { DocumentPreview } from '@/components/job/DocumentPreview/DocumentPreview';
import { useAssignment, useSubmitAssignment } from '@/api/worker';
import { ROUTES } from '@/constants/routes';
import type { ProofSchemaField } from '@/types/worker';
import styles from './AssignmentDetailPage.module.css';

export function AssignmentDetailPage() {
    const { assignmentId } = useParams();
    const navigate = useNavigate();
    const { data, isLoading } = useAssignment(assignmentId!);
    const submitMutation = useSubmitAssignment();
    const [showSimpleConfirm, setShowSimpleConfirm] = useState(false);

    if (isLoading) {
        return (
            <div className={styles.container}>
                <Skeleton width={180} height={14} style={{ marginBottom: 16 }} />
                <Card className={styles.card}>
                    {/* Header: title + status/reward */}
                    <div className={styles.header}>
                        <Skeleton width="55%" height={28} />
                        <div className={styles.meta}>
                            <Skeleton width={90} height={24} variant="rounded" />
                            <Skeleton width={110} height={24} variant="rounded" />
                        </div>
                    </div>

                    {/* Description */}
                    <div className={styles.section}>
                        <Skeleton width={120} height={13} style={{ marginBottom: 10 }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="95%" />
                            <Skeleton variant="text" width="65%" />
                        </div>
                    </div>

                    {/* Instructions */}
                    <div className={styles.section}>
                        <Skeleton width={140} height={13} style={{ marginBottom: 10 }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="90%" />
                            <Skeleton variant="text" width="80%" />
                        </div>
                    </div>

                    {/* Professions */}
                    <div className={styles.section}>
                        <Skeleton width={110} height={13} style={{ marginBottom: 10 }} />
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Skeleton width={100} height={28} variant="rounded" />
                            <Skeleton width={80} height={28} variant="rounded" />
                        </div>
                    </div>

                    {/* Documents */}
                    <div className={styles.section}>
                        <Skeleton width={120} height={13} style={{ marginBottom: 10 }} />
                        <Skeleton width="100%" height={56} variant="card" />
                    </div>

                    {/* Submit section */}
                    <div className={styles.section}>
                        <Skeleton width={130} height={13} style={{ marginBottom: 10 }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <Skeleton width="100%" height={44} variant="card" />
                            <Skeleton width="100%" height={44} variant="card" />
                            <Skeleton width="100%" height={100} variant="card" />
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (!data) {
        return (
            <div className={styles.notFound}>
                <h2>Asignacion no encontrada</h2>
                <Button onClick={() => navigate(ROUTES.assignments)}>Volver a mis trabajos</Button>
            </div>
        );
    }

    const { assignment, job } = data;
    const proofFields: ProofSchemaField[] = job.proof_schema?.fields || [];
    const canSubmit = assignment.status === 'assigned';
    const hasSubmitted = ['submitted', 'approved', 'rejected'].includes(assignment.status);

    const handleSubmit = (result: Record<string, unknown>) => {
        submitMutation.mutate({
            assignmentId: assignment.uuid,
            result,
        });
    };

    return (
        <div className={styles.container}>
            <SEO title={job.title} description="Detalle de tu asignacion en Chamba." path={`/assignments/${assignment.uuid}`} noindex />
            <button className={styles.back} onClick={() => navigate(ROUTES.assignments)}>
                <RiArrowLeftLine className={styles.backIcon} />
                Volver a mis trabajos
            </button>

            <Card className={styles.card}>
                <div className={styles.header}>
                    <div>
                        <h1 className={styles.title}>{job.title}</h1>
                        <div className={styles.meta}>
                            <StatusBadge status={assignment.status} />
                            <RewardDisplay amount={job.reward_amount} currency={job.currency} />
                        </div>
                    </div>
                </div>

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Descripcion</h3>
                    <p className={styles.text}>{job.description}</p>
                </div>

                {job.instructions && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Instrucciones</h3>
                        <p className={styles.text}>{job.instructions}</p>
                    </div>
                )}

                {job.required_professions.length > 0 && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Profesiones</h3>
                        <div className={styles.tags}>
                            {job.required_professions.map((p) => (
                                <JobBadge key={p.uuid} profession={p} />
                            ))}
                        </div>
                    </div>
                )}

                {job.documents.length > 0 && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Documentos</h3>
                        <div className={styles.docGrid}>
                            {job.documents.map((doc) => (
                                <DocumentPreview key={doc.uuid} document={doc} />
                            ))}
                        </div>
                    </div>
                )}

                {canSubmit && proofFields.length > 0 && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Enviar Trabajo</h3>
                        <ProofSchemaForm
                            fields={proofFields}
                            onSubmit={handleSubmit}
                            isLoading={submitMutation.isPending}
                        />
                    </div>
                )}

                {canSubmit && proofFields.length === 0 && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Enviar Trabajo</h3>
                        {!showSimpleConfirm ? (
                            <div className={styles.simpleSubmit}>
                                <RiCheckboxCircleLine className={styles.simpleIcon} />
                                <p className={styles.text}>Este trabajo no requiere informacion adicional. Cuando termines, marca como completado.</p>
                                <Button
                                    variant="primary"
                                    onClick={() => setShowSimpleConfirm(true)}
                                    disabled={submitMutation.isPending}
                                >
                                    Marcar como completado
                                </Button>
                            </div>
                        ) : (
                            <div className={styles.confirmBox}>
                                <p className={styles.confirmText}>
                                    ¿Confirmas que completaste este trabajo? Esta accion no se puede deshacer.
                                </p>
                                <div className={styles.confirmActions}>
                                    <Button
                                        variant="secondary"
                                        onClick={() => setShowSimpleConfirm(false)}
                                        disabled={submitMutation.isPending}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={() => handleSubmit({})}
                                        disabled={submitMutation.isPending}
                                    >
                                        {submitMutation.isPending ? 'Enviando...' : 'Confirmar envio'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {hasSubmitted && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Resultado Enviado</h3>
                        <div className={styles.resultBox}>
                            {Object.entries(assignment.result).map(([key, value]) => (
                                <div key={key} className={styles.resultField}>
                                    <span className={styles.resultLabel}>{key}</span>
                                    <span className={styles.resultValue}>{String(value)}</span>
                                </div>
                            ))}
                            {Object.keys(assignment.result).length === 0 && (
                                <p className={styles.textMuted}>Sin datos adicionales.</p>
                            )}
                        </div>
                    </div>
                )}

                {assignment.review_note && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Nota de Revision</h3>
                        <div className={`${styles.reviewNote} ${assignment.status === 'rejected' ? styles.reviewRejected : styles.reviewApproved}`}>
                            {assignment.review_note}
                        </div>
                    </div>
                )}

                {assignment.payout_amount && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Pago</h3>
                        <RewardDisplay amount={assignment.payout_amount} size="lg" />
                    </div>
                )}
            </Card>
        </div>
    );
}
